/** Shared geometry utilities for map calculations. */

import type { GeoJSONGeometry } from "../types";

/** Extract an array of [lng, lat] coordinates from a line geometry. */
export function extractLineCoordinates(geometry: GeoJSONGeometry): number[][] {
  if (geometry.type === "LineString") {
    return geometry.coordinates as number[][];
  }
  if (geometry.type === "MultiLineString") {
    return (geometry.coordinates as number[][][]).flat();
  }
  return [];
}

/** Extract line parts from a line geometry, preserving MultiLineString separation. */
export function extractLineParts(geometry: GeoJSONGeometry): number[][][] {
  if (geometry.type === "LineString") {
    return [geometry.coordinates as number[][]];
  }
  if (geometry.type === "MultiLineString") {
    return geometry.coordinates as number[][][];
  }
  return [];
}

/** Haversine distance between two points in meters. */
export function haversineMeters(
  lng1: number,
  lat1: number,
  lng2: number,
  lat2: number,
): number {
  const R = 6371000; // Earth radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Total length of a line geometry in meters (sum of segment haversine distances). */
export function lineLengthMeters(geometry: GeoJSONGeometry): number {
  const coords = extractLineCoordinates(geometry);
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    total += haversineMeters(
      coords[i][0],
      coords[i][1],
      coords[i + 1][0],
      coords[i + 1][1],
    );
  }
  return total;
}

/** Check if any vertex of a line geometry is within radiusMeters of a point. */
export function lineIntersectsCircle(
  geometry: GeoJSONGeometry,
  centerLng: number,
  centerLat: number,
  radiusMeters: number,
): boolean {
  const coords = extractLineCoordinates(geometry);
  for (const [lng, lat] of coords) {
    if (haversineMeters(lng, lat, centerLng, centerLat) <= radiusMeters) {
      return true;
    }
  }
  return false;
}

/** Close a ring by appending the first point if not already closed. */
export function closeRing(points: Array<[number, number]>): number[][] {
  if (points.length === 0) return [];
  const ring = points.map((p) => [p[0], p[1]]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]]);
  }
  return ring;
}

/** Convert map bounds to a polygon ring. */
export function boundsToPolygonRing(bounds: {
  west: number;
  south: number;
  east: number;
  north: number;
}): number[][] {
  return [
    [bounds.west, bounds.south],
    [bounds.east, bounds.south],
    [bounds.east, bounds.north],
    [bounds.west, bounds.north],
    [bounds.west, bounds.south],
  ];
}

/** Point-in-polygon test (ray casting). */
function pointInPolygon(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Segment intersection test. */
function segmentsIntersect(
  p1: number[], p2: number[], p3: number[], p4: number[],
): boolean {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  return false;
}

function cross(o: number[], a: number[], b: number[]): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

/** Check if a line geometry intersects a polygon ring.
 *  Handles LineString and MultiLineString.
 *  Uses vertex-in-polygon + segment-edge intersection tests.
 */
export function lineIntersectsPolygon(
  geometry: GeoJSONGeometry,
  polygonRing: number[][],
): boolean {
  const parts = extractLineParts(geometry);
  for (const part of parts) {
    // Check if any vertex is inside the polygon
    for (const [lng, lat] of part) {
      if (pointInPolygon(lng, lat, polygonRing)) return true;
    }
    // Check if any line segment intersects any polygon edge
    for (let i = 0; i < part.length - 1; i++) {
      for (let j = 0; j < polygonRing.length - 1; j++) {
        if (segmentsIntersect(
          part[i], part[i + 1],
          polygonRing[j], polygonRing[j + 1],
        )) {
          return true;
        }
      }
    }
  }
  return false;
}

/** Point-in-MultiPolygon test with hole support. Each polygon's first ring is
 *  its outer boundary; any further rings are holes cut out of it. */
export function pointInMultiPolygon(
  lng: number,
  lat: number,
  multiPolygon: number[][][][],
): boolean {
  for (const polygon of multiPolygon) {
    if (polygon.length === 0) continue;
    if (!pointInPolygon(lng, lat, polygon[0])) continue;
    const inHole = polygon.slice(1).some((hole) => pointInPolygon(lng, lat, hole));
    if (!inHole) return true;
  }
  return false;
}

export interface LineRun {
  coordinates: number[][];
  inside: boolean;
}

/** Split a line geometry into runs of vertices classified inside/outside a
 *  multipolygon. Classification is per-vertex; when it flips between two
 *  consecutive vertices, both the outgoing and incoming run keep that vertex
 *  so the two runs share an endpoint and leave no visual gap between them. */
export function splitLineByMultiPolygon(
  geometry: GeoJSONGeometry,
  multiPolygon: number[][][][],
): LineRun[] {
  const runs: LineRun[] = [];
  const parts = extractLineParts(geometry);

  for (const part of parts) {
    if (part.length === 0) continue;
    if (part.length === 1) {
      const [lng, lat] = part[0];
      runs.push({ coordinates: part, inside: pointInMultiPolygon(lng, lat, multiPolygon) });
      continue;
    }

    let current: number[][] = [part[0]];
    let currentInside = pointInMultiPolygon(part[0][0], part[0][1], multiPolygon);

    for (let i = 1; i < part.length; i++) {
      const vertex = part[i];
      const vertexInside = pointInMultiPolygon(vertex[0], vertex[1], multiPolygon);
      current.push(vertex);
      if (vertexInside !== currentInside) {
        runs.push({ coordinates: current, inside: currentInside });
        current = [vertex];
        currentInside = vertexInside;
      }
    }
    runs.push({ coordinates: current, inside: currentInside });
  }

  return runs;
}

/** Build a GeoJSON Polygon approximating a circle. */
export function buildCirclePolygon(
  centerLng: number,
  centerLat: number,
  radiusMeters: number,
): number[][][] {
  const segments = 64;
  const ring: number[][] = [];
  const latRad = (centerLat * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos(latRad);

  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * 2 * Math.PI;
    const dLng = (radiusMeters * Math.cos(angle)) / metersPerDegLng;
    const dLat = (radiusMeters * Math.sin(angle)) / metersPerDegLat;
    ring.push([centerLng + dLng, centerLat + dLat]);
  }

  return [ring];
}